import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// MUI Komponenten importieren - Fab und AddIcon wurden hinzugefügt
import {
    Container, Box, Typography, Paper, TableContainer, Table, TableHead,
    TableRow, TableCell, TableBody, FormControl, InputLabel, Select,
    MenuItem, CircularProgress, Alert, IconButton, Fab,
    Accordion, AccordionSummary, AccordionDetails, Button
} from '@mui/material';
import { MdEdit, MdBlock, MdCheckCircle, MdCalendarToday } from "react-icons/md";
import AddIcon from '@mui/icons-material/Add'; // Plus-Icon importieren
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';


import { API_BASE_URL } from '../../App.js';
import ConfirmModal from '../../components/ConfirmModal';
import EventModal from './components/EventModal.jsx';
import EventBookingsModal from './components/EventBookingsModal.jsx';
import EventDetailsModal from './components/EventDetailsModal.jsx';

// Die Formatierungsfunktionen
const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};


const Events = () => {
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question'
    });
    const [events, setEvents] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBookingsModalOpen, setIsBookingsModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [selectedEvent, setSelectedEvent] = useState(null);

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedEvent(null);
        fetchEvents();
    };

    const showModal = () => {
        setSelectedEvent(null);
        setIsModalOpen(true);
    };

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/events`);
            setEvents(response.data);
        } catch (err) {
            setError('Fehler beim Laden der Termine. Bitte versuchen Sie es später erneut.');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchEvents();
    }, []);


    const eventTypes = useMemo(() => {
        const types = new Set(events.map(event => event.type || event.eventType).filter(Boolean));
        return ['all', ...types];
    }, [events]);

    const { activeEvents, archivedEvents } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const active = [];
        const archived = [];

        events.forEach(event => {
            const eventEndDate = new Date(event.endDate);
            eventEndDate.setHours(0, 0, 0, 0);

            if (eventEndDate >= today) {
                active.push(event);
            } else {
                archived.push(event);
            }
        });

        // Filter anwenden
        const filterFn = (event) => filterType === 'all' ? true : (event.type === filterType || event.eventType === filterType);

        return {
            activeEvents: active.filter(filterFn),
            archivedEvents: archived.filter(filterFn).sort((a,b) => new Date(b.startDate) - new Date(a.startDate)) // Neueste zuletzt beendete oben
        };
    }, [events, filterType]);

    const getStatusText = (status) => {
        switch (status) {
            case 0: return 'Aktiv';
            case 1: return 'Abgesagt';
            case 2: return 'Anstehend';
            default: return 'Unbekannt';
        }
    };

    const handleToggleStatus = (id, currentStatus) => {
        const action = currentStatus === 0 ? 'deaktivieren' : 'reaktivieren';
        setConfirmConfig({
            open: true,
            title: `Termin ${action}`,
            message: `Möchtest du diesen Termin wirklich ${action}? Alle zukünftigen Termine werden entsprechend geändert.`,
            onConfirm: () => executeToggleStatus(id),
            type: 'warning'
        });
    };

    const executeToggleStatus = async (id) => {
        try {
            const response = await axios.patch(`${API_BASE_URL}/api/events/${id}/toggle-status`);
            toast.success(response.data.message);
            fetchEvents();
        } catch (err) {
            toast.error("Fehler beim Ändern des Status");
        }
    };

    const handleEditEvent = (event) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
    };

    const handleOpenBookings = (event) => {
        if (!event.bookingCount || event.bookingCount === 0) {
            toast.info("Dieser Termin hat keine Buchungsslots.");
            return;
        }
        setSelectedEvent(event);
        setIsBookingsModalOpen(true);
    };

    const handleCloseBookings = () => {
        setIsBookingsModalOpen(false);
        setSelectedEvent(null);
    };

    const handleOpenDetails = (event) => {
        setSelectedEvent(event);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedEvent(null);
    };

    const renderEventTable = (eventList, isArchive = false) => (
        <TableContainer>
            <Table aria-label="event table" size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Name</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Plätze</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Uhrzeit</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Zeitraum</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Kategorie</TableCell>
                        {!isArchive && <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Aktionen</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {eventList.map((event) => (
                        <TableRow key={event._id} hover sx={isArchive ? { opacity: 0.7 } : {}}>
                            <TableCell component="th" scope="row" sx={{ fontSize: '0.875rem' }}>
                                {event.name || 'N/A'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>{event.courts?.join(', ') || 'N/A'}</TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{formatTime(event.startTime)} - {formatTime(event.endTime)}</TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>
                                {formatDate(event.startDate) === formatDate(event.endDate) 
                                    ? formatDate(event.startDate) 
                                    : `${formatDate(event.startDate)} - ${formatDate(event.endDate)}`}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>{event.type || event.eventType || 'N/A'}</TableCell>
                            {!isArchive && <TableCell align="center" sx={{ fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>{getStatusText(event.status)}</TableCell>}
                            <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {(event.bookingCount > 0) && (
                                        <IconButton sx={{ display: { xs: 'none', md: 'inline-flex' } }} 
                                            size="small" 
                                            aria-label="bookings" 
                                            onClick={(e) => { e.stopPropagation(); handleOpenBookings(event); }} 
                                            color="info"
                                            title="Daten anzeigen"
                                        >
                                            <MdCalendarToday />
                                        </IconButton>
                                    )}
                                    {!isArchive && (
                                        <IconButton 
                                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(event._id, event.status); }} 
                                            color={event.status === 0 ? "warning" : "success"} 
                                            size="small"
                                            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                                        >
                                            {event.status === 0 ? <MdBlock title="Deaktivieren" /> : <MdCheckCircle title="Aktivieren" />}
                                        </IconButton>
                                    )}
                                    <IconButton 
                                        size="small" 
                                        aria-label="details" 
                                        onClick={(e) => { e.stopPropagation(); handleOpenDetails(event); }}
                                        title="Details & Aktionen"
                                    >
                                        <MdEdit />
                                    </IconButton>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Container maxWidth="lg" disableGutters>
            <EventModal isOpen={isModalOpen} onClose={closeModal} eventToEdit={selectedEvent} />
            <EventBookingsModal isOpen={isBookingsModalOpen} onClose={handleCloseBookings} event={selectedEvent} />
            <EventDetailsModal 
                isOpen={isDetailsModalOpen} 
                onClose={handleCloseDetails} 
                event={selectedEvent}
                onEdit={handleEditEvent}
                onRefresh={fetchEvents}
            />

            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 2 
                }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
                        Aktuelle Termine
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                        <Fab color="primary" aria-label="add event" onClick={showModal} size="small">
                            <AddIcon />
                        </Fab>

                        <FormControl sx={{ minWidth: 120, flex: { xs: 1, md: 'none' }, maxWidth: { md: 200 } }} size="small">
                            <InputLabel id="event-type-filter-label">Kategorie</InputLabel>
                            <Select
                                labelId="event-type-filter-label"
                                value={filterType}
                                label="Kategorie"
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                {eventTypes.map(type => (
                                    <MenuItem key={type} value={type}>
                                        {type === 'all' ? 'Alle anzeigen' : type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    activeEvents.length > 0 ? renderEventTable(activeEvents) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Keine aktiven Termine gefunden.
                        </Typography>
                    )
                )}
            </Paper>

            {!loading && archivedEvents.length > 0 && (
                <Accordion sx={{ boxShadow: 1, borderRadius: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                            <Typography variant="h6" color="text.secondary">
                                Archiv
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                ({archivedEvents.length} abgelaufene Termine)
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        {renderEventTable(archivedEvents, true)}
                    </AccordionDetails>
                </Accordion>
            )}
        </Container>
    );
};

export default Events;